import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import ChevronLeftLargeIcon from '@atlaskit/icon/core/chevron-left';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';

import {
	ADMIN_COMPANY_HUB_PERMISSIONS,
	COMPANY_HUB,
	COMPANY_HUB_EDIT,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useHistory, useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

const i18n = defineMessages({
	spaceSettingsLink: {
		id: 'breadcrumbs.navigation.space-settings-breadcrumb-link.landing-page',
		defaultMessage: 'Space settings',
		description: 'Text for a breadcrumb link back to Space Settings overview page',
	},
	hubLink: {
		id: 'breadcrumbs.navigation.space-settings-breadcrumb-link.hub-name',
		defaultMessage: '{hubName, select, other {{hubName}} undefined {Company Hub}}',
		description: 'Text for a breadcrumb link back to Company Hub Landing Page editor',
	},
	hubSettingsLink: {
		id: 'breadcrumbs.navigation.space-settings-breadcrumb-link.hub-settings',
		defaultMessage: 'Company Hub settings',
		description: 'Text for a breadcrumb link back to the "Company Hub settings" screen',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BreadcrumbLinkStyledText = styled.span({
	fontWeight: token('font.weight.regular'),
	color: token('color.text.subtle'),
	paddingTop: token('space.025'),
	display: 'inline-block',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'none',
	},
});

type SpaceSettingsBreadcrumbsProps = {
	spaceKey: string;
} & Pick<LinkProps, 'currentSection' | 'source'>;

export function SpaceSettingsBreadcrumbs({
	spaceKey,
	...props
}: SpaceSettingsBreadcrumbsProps): React.JSX.Element | null {
	if (isCompanyHubSpaceKey(spaceKey)) {
		return <HubLink {...props} />;
	}

	return null;
}

type LinkProps = {
	currentSection: string | null | undefined;
	href: string;
	source: string;
	children?: React.ReactNode;
};

function Link({ currentSection, href, source, children }: LinkProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'spaceSettingsBreadcrumbLink',
				source,
				attributes: {
					currentSection,
				},
			},
		}).fire();
	};

	return (
		<nav>
			<AkButton
				href={href}
				onClick={onClick}
				iconBefore={<ChevronLeftLargeIcon label="" color={token('color.icon')} size="small" />}
				appearance="subtle-link"
				spacing="none"
			>
				<BreadcrumbLinkStyledText>{children}</BreadcrumbLinkStyledText>
			</AkButton>
		</nav>
	);
}

type HubLinkProps = Omit<LinkProps, 'href'>;

function HubLink(props: HubLinkProps) {
	const { current } = useRouteDataRef();
	const routeMatch = { name: current?.routeName, params: current?.routeParams };

	const history = useHistory();
	const { formatMessage } = useIntl();
	const { companyHubName } = useContext(SPAViewContext);

	let href: LinkProps['href'] | undefined;
	let text: string | undefined;

	// The Company Hub app-like experience doesn't have side nav implemented yet so it needs a breadcrumb/link for now.
	if (routeMatch?.name === ADMIN_COMPANY_HUB_PERMISSIONS.name && routeMatch?.params?.tab) {
		href = ADMIN_COMPANY_HUB_PERMISSIONS.toUrl();
		text = formatMessage(i18n.hubSettingsLink);
	}

	href ??=
		history?.location?.state?.['from'] === COMPANY_HUB_EDIT.name
			? COMPANY_HUB_EDIT.toUrl()
			: COMPANY_HUB.toUrl();
	text ??= formatMessage(i18n.hubLink, { hubName: companyHubName || undefined });

	return (
		<Link {...props} href={href}>
			{text}
		</Link>
	);
}
